import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { extractErrorMessage } from '../../api/errors';
import * as licensesApi from '../../api/licenses';
import {
  LICENSE_TERMS_MONTHS,
  TRIAL_TERMS_DAYS,
  type License,
  type LicenseDetail,
} from '../../api/licenses';
import { listTenants, type Tenant } from '../../api/tenants';
import { LicenseStateChip, UsageBar } from '../../components/license/LicenseSummary';
import { formatDate, formatDateTime } from '../../util/datetime';

type Action = 'renew' | 'limits' | 'sub' | 'revoke' | 'history' | 'reissue';

const optionalInt = (v: string): number | null => (v.trim() === '' ? null : Number(v));

const EVENT_LABEL: Record<string, string> = {
  issued: 'Issued',
  renewed: 'Renewed',
  revoked: 'Revoked',
  limits_changed: 'Limits changed',
  sub_added: 'Sub-license added',
  sub_revoked: 'Sub-license revoked',
};

/**
 * Licenses (TENANT-LICENSES) — for whoever may issue them: platform admins
 * today, the sales role later. One row per main license, its sub-licenses
 * listed under the holder; tenants with no license are listed below with an
 * Issue button. Notes are the issuer's and never shown to the tenant.
 */
export function Licenses() {
  const [licenses, setLicenses] = useState<License[] | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [issueFor, setIssueFor] = useState<Tenant | null | 'pick'>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; license: License } | null>(null);
  const [action, setAction] = useState<{ kind: Action; license: License } | null>(null);

  const reload = useCallback(async () => {
    try {
      const [ls, ts] = await Promise.all([licensesApi.listLicenses(), listTenants()]);
      setLicenses(ls);
      setTenants(ts.filter((t) => !t.deleted_at));
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'could not load licenses'));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const licensed = useMemo(() => {
    const ids = new Set<string>();
    for (const l of licenses ?? []) {
      ids.add(l.tenant_id);
      for (const s of l.subs) ids.add(s.tenant_id);
    }
    return ids;
  }, [licenses]);
  const unlicensed = tenants.filter((t) => !licensed.has(t.id));
  // A tenant whose license was revoked can be issued a new one (it is
  // read-only until then) — so it is offered alongside the unlicensed ones.
  const revokedIds = useMemo(
    () => new Set((licenses ?? []).filter((l) => l.revoked_at).map((l) => l.tenant_id)),
    [licenses],
  );
  const issuable = tenants.filter((t) => !licensed.has(t.id) || revokedIds.has(t.id));

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" component="h1">
          Licenses
        </Typography>
        <Button variant="contained" onClick={() => setIssueFor('pick')}>
          Issue license
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {licenses === null ? (
        <CircularProgress aria-label="Loading" />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Holder</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ends</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {licenses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No licenses issued yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {licenses.map((l) => (
                <TableRow key={l.id} sx={{ verticalAlign: 'top' }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {l.tenant_name}
                    </Typography>
                    {l.subs.length > 0 && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Also covers: {l.subs.map((s) => s.tenant_name).join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <code>{l.key}</code>
                    {l.kind === 'trial' && (
                      <Chip size="small" variant="outlined" label="Trial" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <LicenseStateChip license={l} />
                  </TableCell>
                  <TableCell>{l.ends_at ? formatDate(l.ends_at) : '—'}</TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <UsageBar label="participants" used={l.seats_used} limit={l.max_seats}
                        level={l.seat_level} />
                      <UsageBar label="devices" used={l.devices_used} limit={l.max_devices}
                        level={l.device_level} />
                      <UsageBar label="procedures" used={l.procedures_used}
                        limit={l.max_procedures} level={l.procedure_level} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={`Actions for ${l.tenant_name}`}
                      onClick={(e) => setMenu({ anchor: e.currentTarget, license: l })}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {unlicensed.length > 0 && (
        <Box>
          <Typography variant="h6" component="h2" gutterBottom>
            Unlicensed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These organisations have no license and are not limited. Issue one, or add them
            under another organisation’s license as a sub-license.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {unlicensed.map((t) => (
              <Chip key={t.id} label={t.name} onClick={() => setIssueFor(t)} />
            ))}
          </Stack>
        </Box>
      )}

      <Menu open={menu !== null} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {(
          (menu?.license.revoked_at
            ? [
                // Revoked = read-only until a new license is issued.
                ['reissue', 'Issue new license'],
                ['history', 'History'],
              ]
            : [
                ['renew', 'Renew'],
                ['limits', 'Change limits'],
                ['sub', 'Add sub-license'],
                ['history', 'History'],
                ['revoke', 'Revoke'],
              ]) as [Action, string][]
        ).map(([kind, label]) => (
          <MenuItem
            key={kind}
            onClick={() => {
              if (menu && kind === 'reissue') {
                setIssueFor(tenants.find((t) => t.id === menu.license.tenant_id) ?? 'pick');
              } else if (menu) {
                setAction({ kind, license: menu.license });
              }
              setMenu(null);
            }}
          >
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {issueFor !== null && (
        <IssueDialog
          tenants={
            issueFor !== 'pick' && !issuable.some((t) => t.id === issueFor.id)
              ? [issueFor, ...issuable]
              : issuable
          }
          revokedIds={revokedIds}
          initial={issueFor === 'pick' ? null : issueFor}
          onClose={() => setIssueFor(null)}
          onDone={() => {
            setIssueFor(null);
            void reload();
          }}
        />
      )}
      {action && (
        <ActionDialog
          action={action.kind}
          license={action.license}
          tenants={issuable}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            void reload();
          }}
        />
      )}
    </Stack>
  );
}

function IssueDialog({
  tenants,
  revokedIds,
  initial,
  onClose,
  onDone,
}: {
  tenants: Tenant[];
  revokedIds: Set<string>;
  initial: Tenant | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [tenant, setTenant] = useState<Tenant | null>(initial);
  const [kind, setKind] = useState<'standard' | 'trial'>('standard');
  const [months, setMonths] = useState(12);
  const [days, setDays] = useState(14);
  const [seats, setSeats] = useState('');
  const [devices, setDevices] = useState('');
  const [procedures, setProcedures] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenant) return;
    setBusy(true);
    try {
      await licensesApi.issueLicense({
        tenant_id: tenant.id,
        ...(kind === 'trial' ? { trial_days: days } : { months }),
        max_seats: optionalInt(seats),
        max_devices: optionalInt(devices),
        max_procedures: optionalInt(procedures),
        notes: notes.trim() || null,
      });
      onDone();
    } catch (err) {
      setError(extractErrorMessage(err, 'could not issue the license'));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>Issue license</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={tenants}
              value={tenant}
              onChange={(_e, v) => setTenant(v)}
              getOptionLabel={(t) => (revokedIds.has(t.id) ? `${t.name} — revoked` : t.name)}
              renderInput={(p) => <TextField {...p} label="Organisation" required />}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={kind}
              onChange={(_e, v) => v && setKind(v)}
              aria-label="License kind"
            >
              <ToggleButton value="standard">License</ToggleButton>
              <ToggleButton value="trial">Trial</ToggleButton>
            </ToggleButtonGroup>
            {kind === 'standard' ? (
              <TextField select label="Term" value={months}
                onChange={(e) => setMonths(Number(e.target.value))}>
                {LICENSE_TERMS_MONTHS.map((m) => (
                  <MenuItem key={m} value={m}>{m} months</MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField select label="Trial length" value={days}
                onChange={(e) => setDays(Number(e.target.value))}>
                {TRIAL_TERMS_DAYS.map((d) => (
                  <MenuItem key={d} value={d}>{d} days</MenuItem>
                ))}
              </TextField>
            )}
            <Stack direction="row" spacing={2}>
              <TextField label="Participant seats" type="number" value={seats}
                onChange={(e) => setSeats(e.target.value)} helperText="Blank = no limit"
                inputProps={{ min: 1 }} fullWidth />
              <TextField label="Devices" type="number" value={devices}
                onChange={(e) => setDevices(e.target.value)} helperText="Phones + appliances"
                inputProps={{ min: 1 }} fullWidth />
              <TextField label="Procedures" type="number" value={procedures}
                onChange={(e) => setProcedures(e.target.value)} helperText="Blank = no limit"
                inputProps={{ min: 1 }} fullWidth />
            </Stack>
            <TextField label="Notes (not shown to the organisation)" value={notes}
              onChange={(e) => setNotes(e.target.value)} multiline minRows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy || !tenant}>
            {busy ? 'Issuing…' : 'Issue'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function ActionDialog({
  action,
  license,
  tenants,
  onClose,
  onDone,
}: {
  action: Action;
  license: License;
  tenants: Tenant[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [months, setMonths] = useState(12);
  const [seats, setSeats] = useState(license.max_seats?.toString() ?? '');
  const [devices, setDevices] = useState(license.max_devices?.toString() ?? '');
  const [procedures, setProcedures] = useState(license.max_procedures?.toString() ?? '');
  const [sub, setSub] = useState<Tenant | null>(null);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState<LicenseDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (action === 'history') {
      licensesApi.getLicense(license.id).then(setDetail, (err) =>
        setError(extractErrorMessage(err, 'could not load the history')),
      );
    }
  }, [action, license.id]);

  const title = {
    reissue: 'Issue new license',
    renew: 'Renew license',
    limits: 'Change limits',
    sub: 'Add sub-license',
    revoke: 'Revoke license',
    history: 'License history',
  }[action];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (action === 'renew') await licensesApi.renewLicense(license.id, months);
      if (action === 'limits')
        await licensesApi.setLicenseLimits(license.id, {
          max_seats: optionalInt(seats),
          max_devices: optionalInt(devices),
          max_procedures: optionalInt(procedures),
        });
      if (action === 'sub' && sub) await licensesApi.addSubLicense(license.id, sub.id);
      if (action === 'revoke') await licensesApi.revokeLicense(license.id, reason.trim() || null);
      onDone();
    } catch (err) {
      setError(extractErrorMessage(err, 'that did not work'));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>
          {title} — {license.tenant_name}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {action === 'renew' && (
              <>
                <Typography variant="body2">
                  Extends the license from {license.state === 'active' && license.ends_at
                    ? formatDate(license.ends_at)
                    : 'today'}. Every sub-license follows.
                </Typography>
                <TextField select label="Extend by" value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}>
                  {LICENSE_TERMS_MONTHS.map((m) => (
                    <MenuItem key={m} value={m}>{m} months</MenuItem>
                  ))}
                </TextField>
              </>
            )}
            {action === 'limits' && (
              <Stack direction="row" spacing={2}>
                <TextField label="Participant seats" type="number" value={seats}
                  onChange={(e) => setSeats(e.target.value)} helperText="Blank = no limit"
                  inputProps={{ min: 1 }} fullWidth />
                <TextField label="Devices" type="number" value={devices}
                  onChange={(e) => setDevices(e.target.value)} helperText="Blank = no limit"
                  inputProps={{ min: 1 }} fullWidth />
                <TextField label="Procedures" type="number" value={procedures}
                  onChange={(e) => setProcedures(e.target.value)} helperText="Blank = no limit"
                  inputProps={{ min: 1 }} fullWidth />
              </Stack>
            )}
            {action === 'sub' && (
              <>
                <Typography variant="body2">
                  The organisation gets its own key; its dates, status and limits follow this
                  license, and its participants and devices count towards this license’s pool.
                </Typography>
                <Autocomplete
                  options={tenants}
                  value={sub}
                  onChange={(_e, v) => setSub(v)}
                  getOptionLabel={(t) => t.name}
                  renderInput={(p) => <TextField {...p} label="Organisation" required />}
                />
              </>
            )}
            {action === 'revoke' && (
              <>
                <Alert severity="warning">
                  The organisation{license.subs.length ? ' and its sub-licenses' : ''} become
                  read-only at once. To restore it, issue a new license.
                </Alert>
                <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              </>
            )}
            {action === 'history' &&
              (detail ? (
                <Stack spacing={1}>
                  {detail.notes && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      <strong>Notes:</strong> {detail.notes}
                    </Typography>
                  )}
                  {detail.events.map((ev, i) => (
                    <Typography key={i} variant="body2">
                      {formatDateTime(ev.at)} · {EVENT_LABEL[ev.action] ?? ev.action}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                !error && <CircularProgress aria-label="Loading" />
              ))}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{action === 'history' ? 'Close' : 'Cancel'}</Button>
          {action !== 'history' && (
            <Button
              type="submit"
              variant="contained"
              color={action === 'revoke' ? 'error' : 'primary'}
              disabled={busy || (action === 'sub' && !sub)}
            >
              {title}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
